import { z } from "zod";
import { manipulateHeaders, CSRF_TOKEN_HEADER } from "@airhelp/http-client/middleware";
const personalDetailsSchema = z.object({
  nationality: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  placeOfBirth: z.string().nullable(),
  profession: z.string().nullable(),
  fiscalNumber: z.string().nullable(),
  idNumber: z.string().nullable(),
  passportNumber: z.string().nullable(),
  occupation: z.string().nullable(),
  martialStatus: z.string().nullable(),
  passportExpirationDate: z.string().nullable()
});
const createUserRequestSchema = z.object({
  user: z.object({
    email: z.string().email(),
    password: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    privacyConsent: z.boolean(),
    marketingConsent: z.boolean(),
    personalDetails: personalDetailsSchema.optional(),
    userProfileUuid: z.string().uuid().optional(),
    locale: z.string().optional()
  })
});
const createUserResponseSchema = z.object({
  user: z.object({
    id: z.number(),
    avatarImageUrl: z.string().optional(),
    countryCode: z.string().nullable(),
    email: z.string().email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    privacyConsentAccepted: z.boolean(),
    marketingConsentAccepted: z.boolean(),
    userProfileUuid: z.string(),
    ahPlusMembership: z.boolean(),
    locale: z.string().nullable(),
    bankTransferDetails: z.unknown().optional(),
    chequeDetails: z.unknown().optional(),
    freeBankTransferDetails: z.unknown().optional(),
    mostRecentAddress: z.unknown().optional(),
    paypalDetails: z.unknown().optional(),
    userDetails: z.unknown().optional(),
    personalDetails: personalDetailsSchema.nullable(),
    phoneNumber: z.object({
      id: z.number(),
      number: z.string().nullable()
    })
  })
});
function createUser({
  client,
  params
}) {
  return client.url("/api/v3/users").snakeCaseBody(createUserRequestSchema.parse(params)).post().valid(createUserResponseSchema);
}
const fetchBrandRequestSchema = z.object({
  name: z.string()
});
const fetchBrandResponseSchema = z.object({
  brand: z.object({
    siteUrl: z.string().optional(),
    logoUrl: z.string(),
    name: z.string(),
    logoPresentInWebapp: z.boolean(),
    logoWidth: z.number(),
    primaryColor: z.string(),
    secondaryColor: z.string(),
    isExpedia: z.boolean(),
    isEgencia: z.boolean(),
    slug: z.string(),
    reloadPii: z.boolean()
  })
});
function fetchBrand({
  client,
  params
}) {
  const { name } = fetchBrandRequestSchema.parse(params);
  return client.get(`/api/brands/${name}`).valid(fetchBrandResponseSchema);
}
const freeBankTransferDetailsSchema = z.object({
  id: z.number().nullable(),
  accountType: z.string().nullable(),
  bankCountryCode: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  iban: z.string().nullable(),
  accountNumber: z.coerce.string().nullable(),
  swift: z.string().nullable(),
  routingNumber: z.coerce.string().nullable(),
  branchCode: z.coerce.string().nullable(),
  bsbCode: z.coerce.string().nullable(),
  sortCode: z.coerce.string().nullable(),
  bankAddress: z.string().nullable(),
  bankCode: z.coerce.string().nullable(),
  bankName: z.string().nullable(),
  address1: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  clabe: z.string().nullable(),
  beneficiaryIdentificationType: z.string().nullable(),
  beneficiaryIdentificationValue: z.string().nullable(),
  supportedCountries: z.array(z.string()),
  usStates: z.array(z.object({ stateCode: z.string(), name: z.string() })),
  caStates: z.array(z.object({ stateCode: z.string(), name: z.string() })),
  mxStates: z.array(z.object({ stateCode: z.string(), name: z.string() })),
  userId: z.number(),
  currency: z.string().nullable(),
  phoneNumber: z.coerce.string().nullable(),
  taxIdentifier: z.string().nullable()
});
const chequeDetailsSchema = z.object({
  id: z.number().nullable(),
  userId: z.number(),
  fullName: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  countryCode: z.string().nullable()
});
const bankTransferDetailsSchema = z.object({
  accountType: z.string(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  bankCountryCode: z.string().nullable(),
  city: z.string().nullable(),
  comment: z.string().nullable(),
  countriesWithMandatoryIban: z.array(z.string()),
  countryCode: z.string().nullable(),
  firstName: z.string().nullable(),
  iban: z.string().nullable(),
  id: z.number().nullable(),
  lastName: z.string().nullable(),
  routingNumber: z.coerce.string().nullable(),
  state: z.string().nullable(),
  swift: z.string().nullable(),
  userId: z.number(),
  zip: z.string().nullable()
});
const claimAirportSchema = z.object({
  id: z.string().nullable(),
  iata: z.string(),
  ec261: z.boolean(),
  name: z.string(),
  countryCode: z.string(),
  city: z.string(),
  cityTranslations: z.record(z.string(), z.string()),
  latitude: z.number(),
  longitude: z.number()
});
const claimAirlineSchema = z.object({
  id: z.string().nullable(),
  uniqueIdentifier: z.string().optional(),
  name: z.string(),
  iata: z.string().nullable(),
  icao: z.string().nullable(),
  code: z.string().nullable(),
  countryCode: z.string(),
  ec261: z.boolean()
});
const claimFlightSchema = z.object({
  id: z.number(),
  itineraryId: z.number(),
  flightNumber: z.string(),
  localDepartureDate: z.string().or(z.coerce.date()),
  distanceKm: z.number().nullable(),
  disrupted: z.boolean(),
  arrivalTime: z.string().nullable().optional(),
  departureTime: z.string().nullable().optional(),
  flightIdentifier: z.string().nullable(),
  departureAirportCode: z.string(),
  arrivalAirportCode: z.string(),
  marketingAirlineName: z.string(),
  operatingAirlineName: z.string().nullable(),
  delayMins: z.number().nullable().optional(),
  isCancelled: z.boolean().nullable().optional(),
  potentiallyMissedConnection: z.boolean().optional(),
  durationMins: z.number().optional(),
  departureScheduled: z.string().nullable().optional(),
  departureAirport: claimAirportSchema,
  arrivalAirport: claimAirportSchema,
  airline: claimAirlineSchema
});
const claimItinerarySchema = z.object({
  id: z.number(),
  links: z.object({
    travelDocuments: z.string().optional()
  }).optional(),
  flights: z.array(claimFlightSchema)
});
const claimAddressSchema = z.object({
  id: z.number(),
  address1: z.string(),
  address2: z.string().nullable(),
  city: z.string(),
  zip: z.string(),
  state: z.string().nullable(),
  countryCode: z.string(),
  formattedAddress: z.string()
});
const compensationInCurrenciesSchema = z.record(
  z.string(),
  z.string().or(z.number())
);
const claimEnquirySchema = z.object({
  id: z.number(),
  arrival: z.string(),
  claimId: z.nullable(z.number()),
  claimType: z.enum(["delayed", "cancelled", "overbooked"]),
  claimValid: z.boolean(),
  compensationInCurrencies: compensationInCurrenciesSchema,
  createdAt: z.string(),
  delay: z.string(),
  delayMins: z.number(),
  departure: z.string(),
  eligibleOnHold: z.boolean(),
  itinerary: claimItinerarySchema,
  processingRegulation: z.string(),
  userPreferredCurrency: z.string(),
  uuid: z.string()
});
const claimStatusSchema = z.custom(
  (value) => typeof value === "string"
);
const fetchCurrentUserProfileResponseSchema = z.object({
  user: z.object({
    id: z.number(),
    countryCode: z.string().optional().nullable(),
    email: z.string().email(),
    firstName: z.string().optional().nullable(),
    lastName: z.string().optional().nullable(),
    privacyConsentAccepted: z.boolean(),
    marketingConsentAccepted: z.boolean(),
    userProfileUuid: z.string(),
    ahPlusMembership: z.boolean(),
    locale: z.string().optional().nullable(),
    phoneNumber: z.object({
      id: z.number(),
      number: z.string().nullable(),
      whatsappConsent: z.boolean().optional().nullable()
    }).optional().nullable(),
    bankTransferDetails: bankTransferDetailsSchema,
    chequeDetails: chequeDetailsSchema,
    freeBankTransferDetails: freeBankTransferDetailsSchema
  })
});
function makeFetchCurrentUserProfileRoute({
  baseUrl = ""
} = {}) {
  return `${baseUrl}/api/users/me`;
}
function fetchCurrentUserProfile({ client }) {
  return client.get(makeFetchCurrentUserProfileRoute()).valid(fetchCurrentUserProfileResponseSchema);
}
const fetchTrustpilotScoreResponseSchema = z.object({
  totalReviews: z.number(),
  scoreValue: z.number()
});
function fetchTrustpilotScore({ client }) {
  return client.get("/api/airhelp_score").valid(fetchTrustpilotScoreResponseSchema);
}
const fetchAirlinesRequestSchema = z.object({
  query: z.string()
});
const airlineSchema = z.object({
  uniqueIdentifier: z.string(),
  name: z.string(),
  alternativeName: z.string().optional().nullable(),
  displayName: z.string(),
  iata: z.string().nullable(),
  iataActive: z.boolean(),
  icao: z.string().nullable(),
  icaoActive: z.boolean(),
  code: z.string(),
  countryCode: z.string(),
  registrationCountryCode: z.string(),
  ec261: z.boolean(),
  active: z.boolean(),
  visible: z.boolean(),
  manuallyAdded: z.boolean()
});
const fetchAirlinesResponseSchema = z.object({
  airlines: z.array(airlineSchema)
});
function fetchAirlines({
  client,
  params
}) {
  const { query } = fetchAirlinesRequestSchema.parse(params);
  return client.url("/api/airlines").query({ q: query }).options(
    {
      // Airlines endpoint requires us not to send auth cookies
      credentials: "omit"
    },
    true
  ).middlewares([
    manipulateHeaders((headers) => {
      headers.delete(CSRF_TOKEN_HEADER);
      return headers;
    })
  ]).get().valid(fetchAirlinesResponseSchema);
}
const fetchAirportsRequestSchema = z.object({
  query: z.string()
});
const airportSchema = z.object({
  searchText: z.string(),
  iata: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  name: z.string(),
  city: z.string(),
  cityTranslations: z.record(z.string(), z.string()),
  country: z.string(),
  countryCode: z.string(),
  ec261: z.boolean(),
  rank: z.number(),
  timeZone: z.string()
});
const fetchAirportsResponseSchema = z.object({
  airports: z.array(airportSchema)
});
function fetchAirports({
  client,
  params
}) {
  const { query } = fetchAirportsRequestSchema.parse(params);
  return client.url(`/api/airports`).query({ q: query }).options(
    {
      // Airports endpoint requires us not to send auth cookies
      credentials: "omit"
    },
    true
  ).middlewares([
    manipulateHeaders((headers) => {
      headers.delete(CSRF_TOKEN_HEADER);
      return headers;
    })
  ]).get().valid(fetchAirportsResponseSchema);
}
const confirmPayoutRequestSchema = z.object({
  claimId: z.string(),
  ahplusPlan: z.boolean(),
  ahplusProduct: z.string().optional(),
  purchaseMembership: z.boolean(),
  promoCode: z.string().optional()
});
async function confirmPayout({
  params,
  client
}) {
  await client.url("/api/payout_confirmation").snakeCaseBody(confirmPayoutRequestSchema.parse(params)).post().res();
}
const createCreditCardRequestSchema = z.object({
  ahplusPlan: z.boolean(),
  ahplusProduct: z.string().optional(),
  claimId: z.string(),
  purchaseMembership: z.boolean(),
  promoCode: z.string().optional()
});
const createCreditCardTransferResponseSchema = z.object({
  creditCardTransferDetails: z.object({
    checkoutId: z.string()
  }).optional(),
  sanctioned: z.boolean().optional()
});
async function createCreditCardTransfer({
  client,
  params
}) {
  return client.url("/api/credit_card_transfer_details").snakeCaseBody(createCreditCardRequestSchema.parse(params)).post().valid(createCreditCardTransferResponseSchema);
}
const fetchCurrencyExchangeRateForTargetCountryRequestSchema = z.object({
  countryCode: z.string(),
  currencyCode: z.string()
});
const fetchCurrencyExchangeRateForTargetCountryTransferResponseSchema = z.object({
  currency: z.string().optional(),
  exchangeRate: z.number().optional()
});
function makeFetchCurrencyExchangeRateForTargetCountryRoute({
  baseUrl = "",
  countryCode,
  currencyCode
} = {}) {
  const params = new URLSearchParams();
  countryCode && params.append("country_code", countryCode);
  currencyCode && params.append("currency", currencyCode);
  return `${baseUrl}/api/free_bank_transfer_details/exchange_currency_rate${params.toString() !== "" ? `?${params.toString()}` : ""}`;
}
async function fetchCurrencyExchangeRateForTargetCountry({
  client,
  params
}) {
  const { countryCode, currencyCode } = fetchCurrencyExchangeRateForTargetCountryRequestSchema.parse(params);
  return client.url(
    makeFetchCurrencyExchangeRateForTargetCountryRoute({
      countryCode,
      currencyCode
    })
  ).get().valid(fetchCurrencyExchangeRateForTargetCountryTransferResponseSchema);
}
const fetchPaymentReferenceDataRequestSchema = z.object({
  countryCode: z.string(),
  sourceCurrency: z.string(),
  targetCurrency: z.string()
});
const fetchPaymentReferenceDataResponseSchema = z.object({
  payoutMethodType: z.string(),
  beneficiaryAddress: z.string(),
  beneficiaryCity: z.string(),
  beneficiaryStateOrProvince: z.string(),
  beneficiaryPostcode: z.string(),
  bankName: z.string(),
  bankCode: z.string(),
  bankCodes: z.record(z.string()),
  bankAddress: z.string(),
  branchCode: z.string(),
  aba: z.string(),
  acctNumber: z.string(),
  bsbCode: z.string(),
  sortCode: z.string(),
  beneficiaryIdentificationType: z.string(),
  beneficiaryIdentificationValue: z.string(),
  clabe: z.string(),
  iban: z.string(),
  bicSwift: z.string(),
  taxId: z.string(),
  phoneNumber: z.string(),
  accountType: z.string()
}).partial();
async function fetchPaymentReferenceData({
  client,
  params
}) {
  const { countryCode, sourceCurrency, targetCurrency } = fetchPaymentReferenceDataRequestSchema.parse(params);
  return client.url(`/api/payments_reference_data/${countryCode}`).query({
    source_currency: sourceCurrency,
    currency: targetCurrency
  }).get().valid(fetchPaymentReferenceDataResponseSchema);
}
const payoutOptions = [
  "bank_transfer",
  "charity",
  "cheque",
  "paypal",
  "payoneer",
  "free_bank_transfer",
  "credit_card_transfer"
];
const payoutOptionsPolicySchema = z.object({
  bankTransfer: z.boolean(),
  charity: z.boolean(),
  cheque: z.boolean(),
  paypal: z.boolean(),
  payoneer: z.boolean(),
  freeBankTransfer: z.boolean(),
  creditCardTransfer: z.boolean()
});
function getEnabledPayoutOptions(payoutOptionsPolicy) {
  return Object.entries(payoutOptionsPolicy).filter(([_, enabled]) => enabled).map(
    ([method]) => ({
      bankTransfer: "bank_transfer",
      charity: "charity",
      cheque: "cheque",
      paypal: "paypal",
      payoneer: "payoneer",
      freeBankTransfer: "free_bank_transfer",
      creditCardTransfer: "credit_card_transfer"
    })[method]
  );
}
const claimPayoutSchema = z.object({
  id: z.number(),
  collectedAt: z.any().pipe(z.coerce.date()).nullable().optional(),
  currency: z.string(),
  customerReimbursementsInPreferredCurrency: z.string(),
  estimatedChequeDeliveryTime: z.string().optional(),
  finalCompensationInPreferredCurrency: z.string(),
  freeOfCharge: z.boolean().optional(),
  legalActionFeeAmountInPreferredCurrency: z.string(),
  legalAction: z.boolean().optional(),
  paid: z.boolean().optional(),
  isPaid: z.boolean().optional(),
  payoutCreated: z.boolean().optional(),
  payoutOptionsFees: z.object({
    bankTransfer: z.number(),
    charity: z.number(),
    cheque: z.number(),
    paypal: z.number(),
    payoneer: z.number(),
    freeBankTransfer: z.number(),
    manualPayout: z.number(),
    creditCardTransfer: z.number()
  }).optional(),
  payoutOptionsPolicy: payoutOptionsPolicySchema.optional(),
  status: z.string().optional(),
  payoutValue: z.string(),
  payoutValueInPreferredCurrency: z.string(),
  priceListVisible: z.boolean().optional(),
  processingFeeInPreferredCurrency: z.number().optional(),
  selectedPayoutOption: z.enum(payoutOptions).nullable().optional(),
  serviceFeeAmountInPreferredCurrency: z.string(),
  userExchangeCurrency: z.string().nullable().optional(),
  userExchangeRate: z.number().nullable().optional(),
  reactFunnel: z.boolean().optional().default(false)
});
const fetchPayoutClaimRequestSchema = z.object({
  claimId: z.number()
});
const fetchPayoutClaimResponseSchema = z.object({
  claim: z.object({
    claimantFirstName: z.string(),
    claimantLastName: z.string(),
    id: z.number(),
    partnerSlug: z.string().nullable(),
    brandSlug: z.string().nullable(),
    claimPayout: claimPayoutSchema,
    address: claimAddressSchema,
    itinerary: claimItinerarySchema
  })
});
const fetchPayoutClaimResultSchema = fetchPayoutClaimResponseSchema.transform(
  ({ claim: { id, ...data } }) => {
    return {
      claimId: id,
      ...data
    };
  }
);
function makeFetchPayoutClaimRoute({
  baseUrl = "",
  claimId
}) {
  return `${baseUrl}/api/claims/${claimId}?document_request_data=true`;
}
async function fetchPayoutClaim({
  client,
  params
}) {
  const { claimId } = fetchPayoutClaimRequestSchema.parse(params);
  return client.url(makeFetchPayoutClaimRoute({ claimId })).get().valid(fetchPayoutClaimResultSchema);
}
const postBankTransferDetailsRequestSchema = z.object({
  accountNumber: z.string().nullable(),
  address1: z.string().nullable(),
  autoCalculatedSwift: z.boolean(),
  bankCountryCode: z.string(),
  city: z.string().nullable(),
  claimId: z.string(),
  currency: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  payoutAmount: z.string(),
  sourceCurrency: z.string(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  accountType: z.string().nullable(),
  bankAddress: z.string().nullable(),
  bankCode: z.string().nullable(),
  bankName: z.string().nullable(),
  beneficiaryAddress: z.string().nullable(),
  beneficiaryFirstName: z.string().nullable(),
  beneficiaryLastName: z.string().nullable(),
  beneficiaryIdentificationType: z.string().nullable(),
  beneficiaryIdentificationValue: z.string().nullable(),
  branchCode: z.string().nullable(),
  bsbCode: z.string().nullable(),
  clabe: z.string().nullable(),
  iban: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  routingNumber: z.coerce.string().nullable(),
  sortCode: z.string().nullable(),
  supportedCountries: z.array(z.string()),
  swift: z.string().nullable(),
  taxIdentifier: z.string().nullable()
});
const postBankTransferDetailsResponseSchema = z.object({
  freeBankTransferDetails: freeBankTransferDetailsSchema
});
async function postBankTransferDetails({
  client,
  params
}) {
  const data = await client.url("/api/free_bank_transfer_details").snakeCaseBody({
    free_bank_transfer_details: postBankTransferDetailsRequestSchema.transform(({ address1, ...payload }) => {
      return {
        ...payload,
        address_1: address1
      };
    }).parse(params)
  }).post().valid(postBankTransferDetailsResponseSchema);
  return data.freeBankTransferDetails;
}
const postChequeDetailsRequestSchema = z.object({
  address1: z.string(),
  address2: z.string().nullable(),
  brandSlug: z.string().nullable(),
  city: z.string(),
  claimId: z.string(),
  countryCode: z.string(),
  fullName: z.string(),
  state: z.string(),
  userId: z.string(),
  zip: z.string()
});
const postChequeDetailsResponseSchema = z.object({
  chequeDetails: z.object({
    id: z.number(),
    userId: z.number(),
    fullName: z.string(),
    address1: z.string(),
    address2: z.string().nullable().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    countryCode: z.string()
  })
});
async function postChequeDetails({
  client,
  params
}) {
  return client.url(`/api/cheque_details`).snakeCaseBody(
    postChequeDetailsRequestSchema.transform(({ address1, address2, ...data }) => ({
      chequeDetails: {
        address_1: address1,
        address_2: address2,
        ...data
      }
    })).parse(params)
  ).post().valid(postChequeDetailsResponseSchema);
}
const postPaypalDetailsRequestSchema = z.object({
  email: z.string(),
  payoutLimit: z.number(),
  removeReceivingLimit: z.boolean(),
  userId: z.string()
});
const postPaypalDetailsResponseSchema = z.object({
  paypalDetails: z.object({
    id: z.number(),
    userId: z.number(),
    verified: z.boolean(),
    removedReceivingLimit: z.boolean(),
    payoutLimit: z.number()
  })
});
async function postPaypalDetails({
  client,
  params
}) {
  return client.url(`/api/paypal_details`).snakeCaseBody(
    postPaypalDetailsRequestSchema.transform((data) => ({
      paypalDetails: data
    })).parse(params)
  ).post().valid(postPaypalDetailsResponseSchema);
}
const putPayoutDetailsRequestSchema = z.object({
  payoutId: z.number(),
  claimPayout: z.object({
    claimId: z.string(),
    freeOfCharge: z.boolean().optional(),
    isPaid: z.boolean().optional(),
    selectedPayoutOption: z.string().optional(),
    serviceFeeAmountInPreferredCurrency: z.number().optional(),
    userExchangeCurrency: z.string().optional(),
    userExchangeRate: z.number().optional()
  })
});
async function putPayoutDetails({
  client,
  params
}) {
  const { payoutId, claimPayout } = putPayoutDetailsRequestSchema.parse(params);
  await client.url(`/api/claim_payouts/${payoutId}`).snakeCaseBody({ claimPayout }).put().res();
}
const fetchClaimRequestSchema = z.object({
  id: z.number()
});
const fellowPassengerSchema = z.object({
  id: z.number(),
  email: z.string().email().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  fullName: z.string(),
  minor: z.boolean()
});
const fetchClaimResponseSchema = z.object({
  claim: z.object({
    address: claimAddressSchema,
    airlineReason: z.string(),
    airlineRequiredDocuments: z.array(z.string()),
    bookingRef: z.string(),
    brandSlug: z.string().nullable(),
    channel: z.string(),
    childClaimId: z.number().nullable(),
    claimEnquiryId: z.number(),
    claimPayout: claimPayoutSchema,
    claimType: z.string(),
    claimant: z.object({
      email: z.string().email(),
      firstName: z.string(),
      fullName: z.string(),
      id: z.number(),
      lastName: z.string(),
      phoneNumber: z.string().nullable()
    }),
    claimantFirstName: z.string(),
    claimantId: z.number(),
    claimantLastName: z.string(),
    cloned: z.boolean(),
    compensationReducedByHalf: z.boolean(),
    createdAt: z.string(),
    delay: z.string().nullable(),
    disruptedFlightAirlineName: z.string(),
    documentForwardingEmailAddress: z.string(),
    eligibleOnHold: z.boolean(),
    extraExpense: z.unknown().nullable(),
    fellowPassengers: z.array(fellowPassengerSchema),
    finalCompensationInPreferredCurrency: z.string(),
    genericLegalState: z.unknown().nullable(),
    goodwillPaymentCreditCard: z.unknown().nullable(),
    id: z.number(),
    itinerary: claimItinerarySchema,
    jurisdiction: z.string().nullable(),
    legalFunnelNdfEligible: z.boolean(),
    legalFunnelNdfUniversal: z.boolean(),
    links: z.object({
      history: z.string(),
      messages: z.string(),
      requestedDocuments: z.string()
    }),
    parentClaimId: z.number().nullable(),
    partnerSlug: z.string().nullable(),
    payoutValue: z.string(),
    payoutValueInPreferredCurrency: z.string(),
    pendingDocumentRequest: z.boolean(),
    pendingDocumentRequestFlow: z.unknown().nullable(),
    regulation: z.string(),
    rejectionReason: z.string().nullable(),
    reprocessed: z.unknown().nullable(),
    requestedDocuments: z.unknown().nullable(),
    selectedJurisdictionCountryCode: z.string().nullable(),
    serviceFeeAmountInPreferred: z.string().optional(),
    state: claimStatusSchema,
    track: z.string(),
    travelDocuments: z.array(
      z.object({
        createdAt: z.string(),
        documentContentType: z.string().nullable(),
        documentFileName: z.string(),
        documentFileSize: z.number().nullable(),
        documentType: z.string().nullable(),
        documentTypeLabel: z.string().nullable(),
        documentUpdatedAt: z.string().nullable(),
        documentUrl: z.string(),
        id: z.number(),
        origin: z.string(),
        referredPassengers: z.array(
          z.object({
            firstName: z.string(),
            id: z.number(),
            lastName: z.string(),
            name: z.string(),
            nameTitle: z.string().nullable(),
            type: z.string(),
            typeLabel: z.string().nullable()
          })
        ),
        state: z.string(),
        userProfileUuid: z.string(),
        webappUserId: z.number()
      })
    ),
    updatedAt: z.string(),
    whatHappened: z.string().nullable()
  })
}).transform(({ claim }) => claim);
function fetchClaim({
  client,
  params
}) {
  const { id } = fetchClaimRequestSchema.parse(params);
  return client.get(`/api/claims/${id}`).valid(fetchClaimResponseSchema);
}
const fetchClaimHistoryRequestSchema = z.object({
  id: z.number()
});
const fetchClaimHistoryResponseSchema = z.object({
  claimHistory: z.object({
    id: z.string(),
    states: z.array(
      z.object({
        state: claimStatusSchema,
        completedAt: z.coerce.date()
      })
    )
  })
}).transform(({ claimHistory: { states } }) => states);
function fetchClaimHistory({
  client,
  params
}) {
  const { id } = fetchClaimHistoryRequestSchema.parse(params);
  return client.get(`/api/claims/${id}/claim_history`).valid(fetchClaimHistoryResponseSchema);
}
const fetchClaimCommunicationHistoryRequestSchema = z.object({
  id: z.number()
});
const fetchClaimCommunicationHistoryResponseSchema = z.object({
  messages: z.array(
    z.object({
      id: z.number(),
      channel: z.string(),
      createdAt: z.coerce.date(),
      subject: z.string(),
      textContent: z.string()
    })
  )
});
function fetchClaimCommunicationHistory({
  client,
  params
}) {
  const { id } = fetchClaimCommunicationHistoryRequestSchema.parse(params);
  return client.get(`/api/claims/${id}/messages`).valid(fetchClaimCommunicationHistoryResponseSchema);
}
const fetchClaimEnquiriesResponseSchema = z.object({
  claimEnquiries: z.array(claimEnquirySchema)
});
function fetchClaimEnquiries({ client }) {
  return client.get("/api/claims_and_flights").valid(fetchClaimEnquiriesResponseSchema);
}
const deleteClaimEnquiryRequestSchema = z.object({
  id: z.number()
});
async function deleteClaimEnquiry({
  client,
  params
}) {
  const { id } = deleteClaimEnquiryRequestSchema.parse(params);
  await client.url(`/api/claim_enquiries/${id}`).delete().res();
}
const updateUserProfileSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string()
});
const updateUserProfileRequestSchema = z.object({
  payload: updateUserProfileSchema
});
async function updateUserProfile({
  client,
  params
}) {
  const { payload } = updateUserProfileRequestSchema.parse(params);
  await client.url("/api/users/update_data").snakeCaseBody({ user: payload }).put().res();
}
const acceptMarketingConsentResponseSchema = z.object({});
function acceptMarketingConsent({ client }) {
  return client.url("/api/users/accept_marketing_consent").patch({}).res();
}
export {
  fetchClaimEnquiries as $,
  fetchPaymentReferenceDataRequestSchema as A,
  fetchPaymentReferenceDataResponseSchema as B,
  fetchPaymentReferenceData as C,
  getEnabledPayoutOptions as D,
  claimPayoutSchema as E,
  fetchPayoutClaimRequestSchema as F,
  makeFetchPayoutClaimRoute as G,
  fetchPayoutClaim as H,
  postBankTransferDetailsRequestSchema as I,
  postBankTransferDetailsResponseSchema as J,
  postBankTransferDetails as K,
  postChequeDetailsRequestSchema as L,
  postChequeDetails as M,
  postPaypalDetailsRequestSchema as N,
  postPaypalDetails as O,
  putPayoutDetailsRequestSchema as P,
  putPayoutDetails as Q,
  fetchClaimRequestSchema as R,
  fetchClaimResponseSchema as S,
  fetchClaim as T,
  fetchClaimHistoryRequestSchema as U,
  fetchClaimHistoryResponseSchema as V,
  fetchClaimHistory as W,
  fetchClaimCommunicationHistoryRequestSchema as X,
  fetchClaimCommunicationHistoryResponseSchema as Y,
  fetchClaimCommunicationHistory as Z,
  fetchClaimEnquiriesResponseSchema as _,
  createUserResponseSchema as a,
  deleteClaimEnquiryRequestSchema as a0,
  deleteClaimEnquiry as a1,
  updateUserProfileSchema as a2,
  updateUserProfileRequestSchema as a3,
  updateUserProfile as a4,
  acceptMarketingConsentResponseSchema as a5,
  acceptMarketingConsent as a6,
  createUser as b,
  createUserRequestSchema as c,
  fetchBrandResponseSchema as d,
  fetchBrand as e,
  fetchBrandRequestSchema as f,
  fetchCurrentUserProfileResponseSchema as g,
  fetchCurrentUserProfile as h,
  fetchTrustpilotScoreResponseSchema as i,
  fetchTrustpilotScore as j,
  fetchAirlinesRequestSchema as k,
  airlineSchema as l,
  makeFetchCurrentUserProfileRoute as m,
  fetchAirlinesResponseSchema as n,
  fetchAirlines as o,
  fetchAirportsRequestSchema as p,
  airportSchema as q,
  fetchAirportsResponseSchema as r,
  fetchAirports as s,
  confirmPayoutRequestSchema as t,
  confirmPayout as u,
  createCreditCardRequestSchema as v,
  createCreditCardTransfer as w,
  fetchCurrencyExchangeRateForTargetCountryRequestSchema as x,
  makeFetchCurrencyExchangeRateForTargetCountryRoute as y,
  fetchCurrencyExchangeRateForTargetCountry as z
};
//# sourceMappingURL=accept-marketing-consent-C0bmbSyk.js.map
